import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  supabase,
  TIER_LABEL,
  TOOLKIT_LABEL,
  type Resource,
  type ResourceSection,
  type Toolkit,
} from '@/lib/supabase'
import { useAuth } from '@/auth/useAuth'
import { invokeFunction } from '@/lib/invoke'
import { Badge, Button, Card, EmptyState, ErrorNote, PageHeader } from '@/components/ui'

const TOOLKITS: Toolkit[] = ['programme', 'training', 'promotion', 'impact']

export function LibraryPage() {
  const { meetsTier } = useAuth()
  const [toolkit, setToolkit] = useState<Toolkit | 'all'>('all')
  const [search, setSearch] = useState('')
  const [opening, setOpening] = useState<string | null>(null)
  // Shown on the card that was clicked: the page is long, so a message at
  // the top would be out of sight.
  const [error, setError] = useState<{ id: string; message: string } | null>(null)

  const { data: resources, isLoading } = useQuery({
    queryKey: ['resources'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('resources')
        .select('id, title, description, toolkit, min_tier, kind, version, version_notes, published_at, section_id, sort_order')
        .order('sort_order')
        .order('title')
      if (error) throw error
      return data as Resource[]
    },
  })

  const { data: sections } = useQuery({
    queryKey: ['resource-sections'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('resource_sections')
        .select('id, title, description, sort_order')
        .order('sort_order')
        .order('title')
      if (error) throw error
      return data as ResourceSection[]
    },
  })

  // R18: search and filter. Both are client side because the library is a few
  // dozen items; move them into the query if it ever gets past a few hundred.
  const visible = (resources ?? []).filter((r) => {
    const matchesToolkit = toolkit === 'all' || r.toolkit === toolkit
    const matchesSearch =
      search.trim() === '' ||
      `${r.title} ${r.description ?? ''}`.toLowerCase().includes(search.toLowerCase())
    return matchesToolkit && matchesSearch
  })

  // Same sections, in the same order, as Ki Tua o Matariki set in the admin
  // screen. Empty sections (after filtering) are skipped; anything without a
  // section is listed last.
  const groups: { key: string; title: string | null; description: string | null; items: Resource[] }[] = [
    ...(sections ?? []).map((sec) => ({
      key: sec.id,
      title: sec.title,
      description: sec.description,
      items: visible.filter((r) => r.section_id === sec.id),
    })),
    {
      key: 'other',
      title: (sections ?? []).length ? 'Other resources' : null,
      description: null,
      items: visible.filter((r) => !(sections ?? []).some((sec) => sec.id === r.section_id)),
    },
  ].filter((g) => g.items.length > 0)

  /**
   * Nothing in this component holds a file URL. It asks the Edge Function,
   * which re-checks tier and subscription against the database and logs the
   * access before signing anything (R12, R20).
   */
  async function openResource(resource: Resource) {
    setOpening(resource.id)
    setError(null)
    // Browsers block window.open once we have awaited the server, so a video
    // gets its tab opened now, inside the click, and pointed at the URL after.
    const videoTab = resource.kind === 'video' ? window.open('', '_blank') : null
    if (videoTab) videoTab.opener = null
    try {
      const data = await invokeFunction<{ url?: string }>('resource-signed-url', {
        resourceId: resource.id,
      })
      if (!data?.url) throw new Error('That resource could not be opened.')
      if (videoTab) videoTab.location.href = data.url
      // A file's signed URL is issued as a download (Content-Disposition:
      // attachment), so navigating to it saves the file and stays on this page.
      else window.location.assign(data.url)
    } catch (e) {
      videoTab?.close()
      // The function's own message says why: tier, lapsed subscription, no file.
      setError({
        id: resource.id,
        message:
          e instanceof Error && e.message
            ? e.message
            : 'That resource could not be opened. If it keeps happening, tell the team.',
      })
    } finally {
      setOpening(null)
    }
  }

  return (
    <>
      <PageHeader title="Resources">
        Everything your subscription includes. Filter by toolkit, or search. Downloads are
        licensed to your organisation for delivering the programme.
      </PageHeader>

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setToolkit('all')}
          className={`rounded-full border px-3 py-1.5 text-sm ${
            toolkit === 'all' ? 'border-pounamu bg-pounamu text-white' : 'border-line text-inksoft'
          }`}
        >
          All
        </button>
        {TOOLKITS.map((t) => (
          <button
            key={t}
            onClick={() => setToolkit(t)}
            className={`rounded-full border px-3 py-1.5 text-sm ${
              toolkit === t ? 'border-pounamu bg-pounamu text-white' : 'border-line text-inksoft'
            }`}
          >
            {TOOLKIT_LABEL[t]}
          </button>
        ))}
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search resources"
          aria-label="Search resources"
          className="ml-auto w-full rounded-sm border border-line px-3 py-1.5 text-sm sm:w-56"
        />
      </div>


      {isLoading && <p className="text-sm text-inksoft">Loading resources…</p>}

      {!isLoading && visible.length === 0 && (
        <EmptyState
          title="Nothing matches that"
          body="Try another toolkit, or clear the search to see everything your tier includes."
        />
      )}

      {groups.map((group) => (
        <section key={group.key} className="mb-8">
          {group.title && <h2 className="mb-1 text-lg text-ink">{group.title}</h2>}
          {group.description && <p className="mb-3 text-sm text-inksoft">{group.description}</p>}
          <div className={`grid gap-4 sm:grid-cols-2 ${group.title && !group.description ? 'mt-3' : ''}`}>
        {group.items.map((resource) => {
          const unlocked = meetsTier(resource.min_tier)
          return (
            <Card key={resource.id} className="flex flex-col">
              <div className="mb-2 flex items-center gap-2">
                <Badge>{TOOLKIT_LABEL[resource.toolkit]}</Badge>
                {resource.kind === 'video' && <Badge>Video</Badge>}
                {!unlocked && <Badge tone="locked">{TIER_LABEL[resource.min_tier]} and above</Badge>}
              </div>

              <h3 className="text-base text-ink">{resource.title}</h3>
              {resource.description && (
                <p className="mt-1 text-sm text-inksoft">{resource.description}</p>
              )}

              {error?.id === resource.id && (
                <div className="mt-3"><ErrorNote>{error.message}</ErrorNote></div>
              )}

              <div className="mt-4 pt-1">
                {unlocked ? (
                  <Button onClick={() => openResource(resource)} disabled={opening === resource.id}>
                    {opening === resource.id
                      ? 'Opening…'
                      : resource.kind === 'video'
                        ? 'Watch'
                        : 'Download'}
                  </Button>
                ) : (
                  // R14: higher-tier material stays visible so partners can see
                  // what a change of tier would add, rather than wondering.
                  <p className="text-sm text-gold">
                    Included from {TIER_LABEL[resource.min_tier]}. Talk to the team about moving up.
                  </p>
                )}
              </div>
            </Card>
          )
        })}
          </div>
        </section>
      ))}
    </>
  )
}
